import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {apiLoading} from "../../../../redux/slice/apiSlice";
import {getContentApproval} from "../../../../api/approval/ContentApprovalService";
import {setApproves} from "../../../../api/approval/ApprovalV2Service";
import {grantApproval} from "../../../../api/approval/ContentApprovalGroupService";
import {inputOnChange} from "../../../../components/commonmethod/simmsInputMethod";
import {handleActiveError, handleActiveToast} from "../../../../components/sonner/ToastBasic";
import {ApprovalType, ContentStatus} from "../../../../constant/CodeSpecNum";
import {
    changeStatusNumericValue,
    putContentApproval,
    resetApprovalProcesses,
    resetApprovalProcessesAfter,
    resetProgressStatusValue,
    setApprovalProcess,
    setApprovalProcessAfter,
    setApprovalProcesses,
    setApprovalProcessesAfter,
} from "../../../../redux/slice/contentSlice";
import {changeRefreshContentStatus} from "../../../../redux/slice/statusSlice";
import ApprovalList from "./ApprovalList";
import ApproveModal from "./approveModal/ApproveModal";
import {
    approvalProcessToApprovers,
    approversToRows,
    approversToSetApprovesRequest,
    buildApprovalProcessSetRequest,
    normalizeApprovalProcess,
    canUserGrantApproval,
    contentApprovalToRows,
    getActiveGroupApprovers,
    isApprovalRegistrationStage,
    shouldShowApprovalComponent,
} from "./approvalUtils";
import {fetchApprovalProcessDetail} from "../shared/approvalProcessLoader";
import "../../../../components/selection/userSelectionChips.css";
import "./approvalComponent.css";

const initApproval = {approvalType: ApprovalType.APPROVED};

function ApprovalShell({title, children, className = "", headAction = null}) {
    return (
        <section className={`approval-comp print-none ${className}`.trim()}>
            {title ? (
                <div className="approval-comp__head">
                    <h3 className="approval-comp__title">{title}</h3>
                    {headAction}
                </div>
            ) : null}
            <div className="approval-comp__inner">{children}</div>
        </section>
    );
}

function PickApproversButton({onClick}) {
    return (
        <button type="button" className="user-selection-chips-view-all" onClick={onClick}>
            결재자 선택
        </button>
    );
}

const APPROVAL_GRANT_INPUT_MIN_H = 48;
const APPROVAL_GRANT_INPUT_MAX_H = 100;

function ApprovalGrantCommentInput({value, onChange, placeholder}) {
    const textareaRef = useRef(null);

    const syncHeight = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;

        textarea.style.height = "auto";
        const nextHeight = Math.min(
            APPROVAL_GRANT_INPUT_MAX_H,
            Math.max(APPROVAL_GRANT_INPUT_MIN_H, textarea.scrollHeight),
        );
        textarea.style.height = `${nextHeight}px`;
    }, []);

    useEffect(() => {
        syncHeight();
    }, [value, syncHeight]);

    return (
        <textarea
            ref={textareaRef}
            id="approval-grant-comment"
            className="approval-comp__grant-input"
            value={value ?? ""}
            onChange={(e) => {
                onChange(e.target.value);
                requestAnimationFrame(syncHeight);
            }}
            onFocus={syncHeight}
            placeholder={placeholder}
            rows={2}
        />
    );
}

function ApprovalStamp({variant, label, selected, onSelect}) {
    return (
        <button
            type="button"
            className={`approval-comp__stamp approval-comp__stamp--${variant}${selected ? " is-selected" : ""}`}
            onClick={onSelect}
            aria-pressed={selected}
            title={`${label} 도장`}
        >
            <span className="approval-comp__stamp-face" aria-hidden="true">
                {label}
            </span>
        </button>
    );
}

function ApprovalGrantPanel({approval, setApproval, onSubmit, resetKey}) {
    const [stampType, setStampType] = useState(null);

    useEffect(() => {
        setStampType(null);
    }, [resetKey]);

    const selectStamp = (type) => {
        setStampType(type);
        inputOnChange(setApproval, "approvalType", type, true);
    };

    const handleSubmit = () => {
        if (!stampType) {
            return;
        }
        onSubmit(stampType);
    };

    const isApprove = stampType === ApprovalType.APPROVED;
    const isReject = stampType === ApprovalType.REJECTED;

    return (
        <div className="approval-comp__grant">
            <div className="approval-comp__grant-toolbar">
                <span className="approval-comp__grant-toolbar-label">결재</span>
                <div className="approval-comp__stamp-row" role="group" aria-label="결재 도장 선택">
                    <ApprovalStamp
                        variant="approve"
                        label="승인"
                        selected={isApprove}
                        onSelect={() => selectStamp(ApprovalType.APPROVED)}
                    />
                    <ApprovalStamp
                        variant="reject"
                        label="반려"
                        selected={isReject}
                        onSelect={() => selectStamp(ApprovalType.REJECTED)}
                    />
                </div>
                <button
                    type="button"
                    className={`approval-comp__grant-submit${stampType ? ` approval-comp__grant-submit--${isApprove ? "approve" : "reject"}` : ""}`}
                    disabled={!stampType}
                    onClick={handleSubmit}
                >
                    결재
                </button>
            </div>
            <ApprovalGrantCommentInput
                value={approval.description}
                onChange={(v) => inputOnChange(setApproval, "description", v, true)}
                placeholder="결재 의견 (선택)"
            />
        </div>
    );
}

function resetApprovalViewState(setters) {
    const {
        setContentApproval,
        setApprovableGroup,
        setContentApprovers,
        setApproval,
    } = setters;

    setContentApproval({});
    setApprovableGroup({});
    setContentApprovers([]);
    setApproval({...initApproval, description: ""});
}

/**
 * ContentApprovalModule 대체
 * - contentApproval 없음: 결재 등록(결재자 선택 모달)
 * - contentApproval 있음: 결재 처리 화면
 */
export default function ApprovalComponent({
    auth = true,
    contentId,
    contentCategory,
    approvalSeparator = false,
    approvalProcessId,
    title,
    className = "",
    readOnly = false,
    onApprovalProcessSet,
    showChipDetail,
    onChipClick,
    ...rest
}) {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth.user);
    const contentStatus = useSelector((state) => state.content.progressStatusValue);

    const [contentApproval, setContentApproval] = useState({});
    const [contentApprovers, setContentApprovers] = useState([]);
    const [approvableGroup, setApprovableGroup] = useState({});
    const [refresh, setRefresh] = useState(false);
    const [approval, setApproval] = useState(initApproval);
    const [approvalLoaded, setApprovalLoaded] = useState(!contentId && !approvalProcessId);

    const [selectedApprovers, setSelectedApprovers] = useState([]);
    const [linkedProcessId, setLinkedProcessId] = useState(approvalProcessId ?? null);
    const [linkedProcessMeta, setLinkedProcessMeta] = useState({name: "", description: ""});
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        void handleCallContentApproval();

        return () => {
            if (!contentId) {
                if (!approvalSeparator) {
                    dispatch(setApprovalProcess({}));
                } else {
                    dispatch(setApprovalProcessAfter({}));
                }
            }
            if (!approvalSeparator) {
                dispatch(resetApprovalProcesses());
            } else {
                dispatch(resetApprovalProcessesAfter());
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh, contentId, contentCategory]);

    const toggleRefresh = () => setRefresh((prev) => !prev);

    const handleCallContentApproval = async () => {
        if (!contentId) {
            resetApprovalViewState({
                setContentApproval,
                setApprovableGroup,
                setContentApprovers,
                setApproval,
            });
            if (!approvalProcessId) {
                setApprovalLoaded(true);
            }
            return;
        }

        setApprovalLoaded(false);
        const response = await getContentApproval({contentId, contentCategory});
        if (response?.status === 200) {
            handleSetData(response.data);
        } else {
            resetApprovalViewState({
                setContentApproval,
                setApprovableGroup,
                setContentApprovers,
                setApproval,
            });
            dispatch(putContentApproval({}));
        }

        setApprovalLoaded(true);
    };

    const handleSetData = (data) => {
        setContentApproval({...data});
        dispatch(putContentApproval({...data}));

        if (data.approvableGroup && Object.keys(data.approvableGroup).length > 0) {
            setApprovableGroup({...data.approvableGroup});
            setContentApprovers([...(data.approvableGroup.contentApprovers ?? [])]);
        } else {
            setApprovableGroup({});
            setContentApprovers([]);
        }

        setApproval({...initApproval, description: ""});
    };

    const handleRefreshApprovalDone = () => {
        dispatch(changeStatusNumericValue(0));
        dispatch(resetProgressStatusValue());
        dispatch(changeRefreshContentStatus());
        setApproval({...initApproval, description: ""});
    };

    const handleGrantApproval = async (approvedUserId, approvedUserName, approvalType) => {
        const isReject = approvalType === ApprovalType.REJECTED;
        const confirmMessage = isReject ? "반려 처리하시겠습니까?" : "승인 처리하시겠습니까?";
        if (!window.confirm(confirmMessage)) return;

        const payload = {
            id: contentApproval.approvableGroup.id,
            approvedUserId,
            approvedUserName,
            contentId,
            contentCategory,
            ...approval,
            approvalType,
        };

        const response = await grantApproval(payload);
        if (response?.status === 200) {
            handleSetData(response.data);
            if (response.data.approvalResult === ApprovalType.DONE) {
                handleActiveToast({message: "결재가 완료되었습니다."});
                handleRefreshApprovalDone();
            }
            if (response.data.approvalResult === ApprovalType.REJECTED) {
                handleActiveToast({message: "결재가 반려됬습니다."});
                handleRefreshApprovalDone();
            }
            return;
        }

        if (response?.status === 201) {
            handleActiveError({message: response.data.title, description: response.data.reason});
            toggleRefresh();
        }
    };

    const applyApprovalProcessResponse = (approvalProcess) => {
        const normalized = normalizeApprovalProcess(approvalProcess);
        if (!normalized?.id) {
            onApprovalProcessSet?.(null);
            return;
        }

        if (!approvalSeparator) {
            dispatch(setApprovalProcesses([normalized]));
            dispatch(setApprovalProcess(normalized));
        } else {
            dispatch(setApprovalProcessesAfter([normalized]));
            dispatch(setApprovalProcessAfter(normalized));
        }

        onApprovalProcessSet?.(normalized);
    };

    useEffect(() => {
        if (contentId) {
            return undefined;
        }

        if (!approvalProcessId) {
            setSelectedApprovers([]);
            setLinkedProcessId(null);
            setLinkedProcessMeta({name: "", description: ""});
            setApprovalLoaded(true);
            return undefined;
        }

        let cancelled = false;
        setApprovalLoaded(false);

        fetchApprovalProcessDetail(approvalProcessId)
            .then((detail) => {
                if (cancelled) {
                    return;
                }

                if (!detail) {
                    setSelectedApprovers([]);
                    setLinkedProcessId(null);
                    setLinkedProcessMeta({name: "", description: ""});
                    return;
                }

                setSelectedApprovers(approvalProcessToApprovers(detail));
                setLinkedProcessId(detail.id ?? approvalProcessId);
                setLinkedProcessMeta({
                    name: detail.name ?? "",
                    description: detail.description ?? "",
                });
                applyApprovalProcessResponse(detail);
            })
            .finally(() => {
                if (!cancelled) {
                    setApprovalLoaded(true);
                }
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contentId, approvalProcessId, approvalSeparator]);

    const handleApproversConfirm = async (approvers) => {
        const requestDto = contentId
            ? approversToSetApprovesRequest(approvers)
            : buildApprovalProcessSetRequest({
                approvalProcessId: linkedProcessId ?? undefined,
                name: linkedProcessMeta?.name,
                description: linkedProcessMeta?.description,
                approvers,
            });

        if (!requestDto.users.length) {
            handleActiveError({message: "결재 라인은 1명 이상 필요합니다."});
            return false;
        }

        dispatch(apiLoading(true));
        try {
            const response = await setApproves(requestDto);

            if (response?.status === 200) {
                const normalized = normalizeApprovalProcess(response.data);
                setSelectedApprovers(approvers);
                setLinkedProcessId(normalized?.id ?? linkedProcessId);
                if (normalized?.name) {
                    setLinkedProcessMeta((prev) => ({
                        ...prev,
                        name: normalized.name,
                        description: normalized.description ?? prev.description,
                    }));
                }
                applyApprovalProcessResponse(normalized);
                // handleActiveToast({message: "결재선이 설정되었습니다."});
                if (contentId) {
                    toggleRefresh();
                }
                return true;
            }

            if (response?.status === 201 || response?.status === 202) {
                handleActiveError({
                    message: response.data?.title ?? "결재선 설정 실패",
                    description: response.data?.reason,
                });
            } else if (response?.response?.status === 400) {
                handleActiveError({
                    message: "결재선 설정 실패",
                    description: response.response?.data?.message ?? response.response?.data,
                });
            } else {
                handleActiveError({message: "결재선 설정에 실패했습니다."});
            }
            return false;
        } finally {
            dispatch(apiLoading(false));
        }
    };

    /** 결재선을 비운다 — 결재선 없이(전결) 문서를 저장하기 위한 경로. 서버 호출 없음. */
    const handleApproversReset = () => {
        setSelectedApprovers([]);
        setLinkedProcessId(null);
        setLinkedProcessMeta({name: "", description: ""});

        if (!approvalSeparator) {
            dispatch(resetApprovalProcesses());
            dispatch(setApprovalProcess({}));
        } else {
            dispatch(resetApprovalProcessesAfter());
            dispatch(setApprovalProcessAfter({}));
        }

        onApprovalProcessSet?.(null);
        handleActiveToast({message: "결재선이 초기화되었습니다."});
    };

    const activeApprovers = useMemo(
        () => getActiveGroupApprovers(contentApproval, approvableGroup, contentApprovers),
        [contentApproval, approvableGroup, contentApprovers],
    );

    const canGrantApproval =
        activeApprovers.length > 0 &&
        canUserGrantApproval(activeApprovers, user) &&
        approvableGroup.approvalType === ApprovalType.PENDING &&
        (!approvalSeparator || contentStatus.statusNumericValue === ContentStatus.POST_APPROVE.statusNumericValue);

    const createRows = useMemo(() => approversToRows(selectedApprovers), [selectedApprovers]);
    const viewRows = useMemo(() => contentApprovalToRows(contentApproval), [contentApproval]);
    const shellTitle = title ?? "결재";
    const hasContentApproval = Object.keys(contentApproval).length > 0;
    const isRegisterMode = !hasContentApproval;
    const isAtRegistrationStage = !contentId
        || isApprovalRegistrationStage(contentStatus, contentCategory, approvalSeparator);
    const canPickApprovers = auth && !readOnly && isAtRegistrationStage;
    const chipDetailEnabled = showChipDetail ?? !isRegisterMode;
    const showApprovalComponent = shouldShowApprovalComponent({
        contentId,
        hasContentApproval,
        contentStatus,
        contentCategory,
        approvalSeparator,
    });

    if ((contentId || approvalProcessId) && !approvalLoaded) {
        return null;
    }

    if (!showApprovalComponent) {
        return null;
    }

    const openApproverModal = () => setModalOpen(true);
    const headAction = canPickApprovers ? (
        <PickApproversButton onClick={openApproverModal} />
    ) : null;

    const registerView = (
        <>
            <ApprovalShell title={shellTitle} className={className} headAction={headAction}>
                <ApprovalList
                    rows={createRows}
                    emptyLabel="결재자를 선택해주세요"
                    showChipDetail={chipDetailEnabled}
                    onChipClick={onChipClick}
                />
            </ApprovalShell>

            <ApproveModal
                open={modalOpen}
                value={selectedApprovers}
                onChange={handleApproversConfirm}
                onReset={handleApproversReset}
                onClose={() => setModalOpen(false)}
            />
        </>
    );

    if (isRegisterMode) {
        return registerView;
    }

    return (
        <ApprovalShell title={shellTitle} className={className}>
            {canGrantApproval && (
                <ApprovalGrantPanel
                    key={approvableGroup?.id}
                    resetKey={approvableGroup?.id}
                    approval={approval}
                    setApproval={setApproval}
                    onSubmit={(approvalType) => handleGrantApproval(user.id, user.name, approvalType)}
                />
            )}

            {contentApproval.approvalResult === ApprovalType.APPROVAL_PASSED ? (
                <div className="approval-comp__passed">전결처리</div>
            ) : (
                <ApprovalList
                    rows={viewRows}
                    activeGroupId={approvableGroup?.id}
                    emptyLabel="결재라인이 없습니다"
                    showChipDetail={chipDetailEnabled}
                    onChipClick={onChipClick}
                />
            )}
        </ApprovalShell>
    );
}
